import { LitElement, TemplateResult, html } from 'lit';
import { customElement, queryAssignedElements, property } from 'lit/decorators.js';
import globalStyles from './styles/outline-employee-card.global.pcss?inline';
import encapsulatedStyles from './styles/outline-employee-card.encapsulated.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import { screenSizeMap } from '../../../controllers/screen-size-controller';
import { validateSlot } from '../../../utilities/slot';
import '../../outline-icon/outline-icon';

export const employeeCardVariations = ['basic', 'expanded'] as const;
export type EmployeeCardVariation = (typeof employeeCardVariations)[number];

const componentName = 'outline-employee-card';

/**
 * The OutlineEmployeeCard component
 * @element outline-employee-card
 * @slot heading - An outline heading component
 * @slot image - An outline image compoenent
 * @slot department - A span
 * @slot title - A span
 * @slot phone - A basic <a> tag
 * @slot email - A basic <a> tag
 */

@customElement('outline-employee-card')
export class OutlineEmployeeCard extends LitElement {
  resizeObserver: ResizeObserver;
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * Ref to elements in the `department` slot
   */
  @queryAssignedElements({slot: 'department'})
  departmentRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `title` slot
   */
  @queryAssignedElements({slot: 'title'})
  titleRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `phone` slot
   */
  @queryAssignedElements({slot: 'phone' })
  phoneRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `email` slot
   */
  @queryAssignedElements({slot: 'email' })
  emailRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `schedule-online` slot
   */
  @queryAssignedElements({slot: 'schedule-online' })
  schOnlineRef: Array<HTMLElement>;
  
  /**
   * Ref to elements in the `schedule-phone` slot
   */
  @queryAssignedElements({slot: 'schedule-phone' })
  schPhoneRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `schedule-inperson` slot
   */
  @queryAssignedElements({slot: 'schedule-inperson' })
  schInPersonRef: Array<HTMLElement>;

  /**
   * If screen is below mobile width
   */
  @property({ type: Boolean, reflect: true })
  isMobile: boolean;

  /**
   * EmployeeCardVariation type
   */
  @property({ type: String })
  variant: EmployeeCardVariation = 'basic';

  /**
   * Ref to this
   */
  _selfRef: HTMLElement;
  
  
  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeAtScreen();
    });
  };
  
  
  firstUpdated() {    
    this.setSelfRef()
    validateSlot(this.departmentRef, '.outline-employee-card--department', this.shadowRoot);
    validateSlot(this.titleRef, '.outline-employee-card--title', this.shadowRoot);
    validateSlot(this.phoneRef, '.outline-employee-card--phone', this.shadowRoot);
    validateSlot(this.emailRef, '.outline-employee-card--email', this.shadowRoot);
    
    if (this.variant==='expanded') {
      validateSlot(this.schOnlineRef, '.outline-employee-card--schedule-online', this.shadowRoot);
      validateSlot(this.schPhoneRef, '.outline-employee-card--schedule-phone', this.shadowRoot);
      validateSlot(this.schInPersonRef, '.outline-employee-card--schedule-inperson', this.shadowRoot);
    }
    this.resizeObserver.observe(this._selfRef);
  }

  setSelfRef() {
    const self = this.shadowRoot?.querySelector('.outline-employee-card') as HTMLElement;
    if (self) {      
      this._selfRef = self;
    }
  }

  resizeAtScreen() {
    this.isMobile = this._selfRef.clientWidth < (screenSizeMap.get('xs') as number);
  }

    render(): TemplateResult {
      return html`
        <section class="outline-employee-card">
          <div class="outline-employee-card--left-side">
            <div class="outline-employee-card--image">
              <slot name="image"></slot>
            </div>
            ${this.isMobile ? this.headingTemplate() : null}
          </div>
          <div class="outline-employee-card--right-side">
            ${!this.isMobile ? this.headingTemplate() : null}
            <div class="outline-employee-card--position">
              <div class="outline-employee-card--department">
                <slot name="department"></slot>
              </div>
              <div class="outline-employee-card--title">
                <slot name="title"></slot>
              </div>
            </div>
            <div class="outline-employee-card--phone">
              <outline-icon icon='<i class="fa-solid fa-phone"></i>' size='1em'></outline-icon>
              <span class="outline-employee-card--phone-location">Office:</span>
              <slot name="phone"></slot>
            </div>
            <div class="outline-employee-card--email">
              <outline-icon icon='<i class="fa-solid fa-envelope"></i>' size='1em'></outline-icon>
              <slot name="email"></slot>
            </div>
            ${this.variant==='expanded' 
              ? this.expandedTemplate()
              : null 
              }
          </div>
       </section>
      `;
    } 

    headingTemplate() {      
      return html `
        <div class="outline-employee-card--heading">
          <slot name="heading"></slot>
        </div>`
    }

    expandedTemplate() {
      return html`
        <div class="outline-employee-card--expanded">
          <span class="outline-employee-card--schedule-label">Schedule a Meeting</span>
          <div class="outline-employee-card--schedule-links">
            <span class="outline-employee-card--schedule-online">
              <slot name="schedule-online"></slot>
            </span>
            <span class="outline-employee-card--schedule-phone">
              <slot name="schedule-phone"></slot>
            </span>
            <span class="outline-employee-card--schedule-inperson">
              <slot name="schedule-inperson"></slot>
            </span>
          </div>
        </div>
      `
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [componentName]: OutlineEmployeeCard;
    }
}